import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import ParrotCreation from './ParrotCreation.jsx';
import { useToast } from '../contexts/ToastContext.jsx';

/**
 * Unified Parrot Creation Component
 * Provides a consistent parrot creation experience across onboarding, admin, and regular use
 * Integrates the enhanced ParrotCreation component with context-specific features
 */
const UnifiedParrotCreation = ({
  // Core props
  onComplete,
  onCancel,
  onError,

  // Context props
  mode = 'standalone', // 'onboarding', 'admin', 'standalone'
  theme = 'light', // 'light', 'dark'

  // Admin-specific props
  assignToUser = false,
  selectedUserId = null,
  users = [],

  // Phone number assignment props
  showPhoneNumberAssignment = false,
  assignPhoneNumbers = false,
  selectedPhoneNumbers = [],
  availablePhoneNumbers = [],

  // Customization props
  initialName = '',
  selectedPhoneNumber = null,
  showNameInput = true,
  showVoiceSelection = true,
  enableProgressSaving = false,
  customSteps = null,

  // Enhanced features
  showAdminOptions = false,
  showUserAssignment = false,
  showQuickActions = false,

  // Callbacks
  onStepChange = null,
  onUserAssignmentChange = null,
  onPhoneNumberAssignmentChange = null
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();

  // State management
  const [loading, setLoading] = useState(false);
  const [adminOptions, setAdminOptions] = useState({
    assignToUser: assignToUser,
    selectedUserId: selectedUserId
  });
  const [phoneNumberOptions, setPhoneNumberOptions] = useState({
    assignPhoneNumbers: assignPhoneNumbers,
    selectedPhoneNumbers: selectedPhoneNumbers
  });
  const [quickTemplateData, setQuickTemplateData] = useState(null);

  // Quick template configurations
  const quickTemplates = {
    business: {
      name: 'Business Assistant',
      suggestedPrompt: 'You are a professional business assistant. You help customers with inquiries about our products and services. Be helpful, courteous, and professional in all interactions.',
      description: 'Perfect for general business inquiries and customer support'
    },
    'customer-service': {
      name: 'Customer Service Rep',
      suggestedPrompt: 'You are a friendly customer service representative. Your goal is to help customers resolve issues, answer questions, and ensure they have a positive experience. Always be patient and understanding.',
      description: 'Specialized for customer support and issue resolution'
    },
    sales: {
      name: 'Sales Assistant',
      suggestedPrompt: 'You are an enthusiastic sales assistant. Your role is to help potential customers understand our products, answer their questions, and guide them toward making a purchase decision. Be persuasive but not pushy.',
      description: 'Optimized for sales conversations and lead qualification'
    },
    receptionist: {
      name: 'Virtual Receptionist',
      suggestedPrompt: 'You are a professional virtual receptionist. You greet callers, direct them to the appropriate department or person, take messages, and provide basic information about the company. Be welcoming and efficient.',
      description: 'Ideal for call routing and basic company information'
    }
  };

  // Handle parrot creation completion with admin features
  const handleParrotComplete = async (parrotData) => {
    try {
      setLoading(true);

      // Handle admin-specific user assignment
      if (mode === 'admin' && adminOptions.assignToUser && adminOptions.selectedUserId && parrotData.parrot_id) {
        const token = await getToken();

        const response = await fetch(`https://parrotmate.com/api/admin/parrots/${parrotData.parrot_id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: adminOptions.selectedUserId
          })
        });

        if (!response.ok) {
          console.warn('Failed to assign parrot to user, but parrot was created successfully');
          showToast('Parrot created but user assignment failed', 'warning');
        } else {
          showToast('Parrot created and assigned successfully', 'success');
        }
      } else {
        showToast('Parrot created successfully', 'success');
      }

      // Call the completion callback with enhanced data
      if (onComplete) {
        onComplete({
          ...parrotData,
          adminOptions: mode === 'admin' ? adminOptions : null,
          phoneNumberOptions: mode === 'admin' ? phoneNumberOptions : null,
          mode: mode
        });
      }

    } catch (error) {
      console.error('Error in unified parrot creation:', error);
      const errorMessage = error.message || 'Failed to complete parrot creation';

      if (onError) {
        onError(errorMessage);
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle admin options change
  const handleAdminOptionsChange = (field, value) => {
    setAdminOptions(prev => ({
      ...prev,
      [field]: value
    }));

    if (onUserAssignmentChange && field === 'assignToUser') {
      onUserAssignmentChange(value, adminOptions.selectedUserId);
    }
  };

  // Handle phone number options change
  const handlePhoneNumberOptionsChange = (field, value) => {
    setPhoneNumberOptions(prev => ({
      ...prev,
      [field]: value
    }));

    if (onPhoneNumberAssignmentChange && field === 'assignPhoneNumbers') {
      onPhoneNumberAssignmentChange(value, phoneNumberOptions.selectedPhoneNumbers);
    } else if (onPhoneNumberAssignmentChange && field === 'selectedPhoneNumbers') {
      onPhoneNumberAssignmentChange(phoneNumberOptions.assignPhoneNumbers, value);
    }
  };

  // Enhanced step change handler
  const handleStepChange = (step, data) => {
    if (onStepChange) {
      onStepChange(step, {
        ...data,
        mode: mode,
        adminOptions: mode === 'admin' ? adminOptions : null,
        phoneNumberOptions: mode === 'admin' ? phoneNumberOptions : null,
        quickTemplate: quickTemplateData
      });
    }
  };

  // Handle quick template selection
  const handleQuickTemplate = (templateKey) => {
    const template = quickTemplates[templateKey];
    if (template) {
      setQuickTemplateData({
        key: templateKey,
        ...template
      });

      showToast(`Applied ${template.name} template`, 'success');

      // You could also trigger a callback to pre-fill the ParrotCreation component
      // This would require extending the ParrotCreation component to accept initial data
    }
  };

  return (
    <div className="unified-parrot-creation">
      {/* Admin Options Panel */}
      {(mode === 'admin' || showAdminOptions) && showUserAssignment && (
        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
          <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Admin Options
          </h4>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="assign_to_user_unified"
                checked={adminOptions.assignToUser}
                onChange={(e) => handleAdminOptionsChange('assignToUser', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded dark:border-gray-600"
              />
              <label
                htmlFor="assign_to_user_unified"
                className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}
              >
                Assign this parrot to a specific user
              </label>
            </div>

            {adminOptions.assignToUser && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  Select User
                </label>
                <select
                  value={adminOptions.selectedUserId}
                  onChange={(e) => handleAdminOptionsChange('selectedUserId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${theme === 'dark'
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name} (${user.email})`
                        : user.email
                      }
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phone Number Assignment Panel */}
      {(mode === 'admin' || showAdminOptions) && showPhoneNumberAssignment && (
        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
          <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Phone Number Assignment
          </h4>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="assign_phone_numbers_unified"
                checked={phoneNumberOptions.assignPhoneNumbers}
                onChange={(e) => handlePhoneNumberOptionsChange('assignPhoneNumbers', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded dark:border-gray-600"
              />
              <label
                htmlFor="assign_phone_numbers_unified"
                className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}
              >
                Assign phone numbers to this parrot
              </label>
            </div>

            {phoneNumberOptions.assignPhoneNumbers && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  Select Phone Numbers
                </label>
                {availablePhoneNumbers.length === 0 ? (
                  <div className={`text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="mb-2">📞</div>
                    <div className="text-sm">No available phone numbers</div>
                    <div className="text-xs mt-1">All assigned numbers are already assigned to parrots</div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {availablePhoneNumbers.map((number) => (
                      <div
                        key={number.id}
                        className={`flex items-center p-2 rounded border transition-colors ${
                          phoneNumberOptions.selectedPhoneNumbers.includes(number.id)
                            ? theme === 'dark'
                              ? 'bg-teal-900/20 border-teal-500'
                              : 'bg-teal-50 border-teal-500'
                            : theme === 'dark'
                              ? 'bg-gray-600 border-gray-500 hover:border-gray-400'
                              : 'bg-white border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`phone_unified_${number.id}`}
                          checked={phoneNumberOptions.selectedPhoneNumbers.includes(number.id)}
                          onChange={(e) => {
                            const newSelection = e.target.checked
                              ? [...phoneNumberOptions.selectedPhoneNumbers, number.id]
                              : phoneNumberOptions.selectedPhoneNumbers.filter(id => id !== number.id);
                            handlePhoneNumberOptionsChange('selectedPhoneNumbers', newSelection);
                          }}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 rounded border-gray-300 dark:border-gray-600"
                        />
                        <label htmlFor={`phone_unified_${number.id}`} className="ml-3 flex-1 cursor-pointer">
                          <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {number.phone_number}
                          </div>
                          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {number.friendly_name}
                          </div>
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                            Assigned to: {number.assigned_user_name}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {phoneNumberOptions.selectedPhoneNumbers.length > 0 && (
                  <div className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {phoneNumberOptions.selectedPhoneNumbers.length} phone number{phoneNumberOptions.selectedPhoneNumbers.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions Panel */}
      {showQuickActions && (
        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
          <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Quick Actions
          </h4>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleQuickTemplate('business')}
              className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
              title="Pre-fill with business assistant configuration"
            >
              Business Assistant Template
            </button>
            <button
              onClick={() => handleQuickTemplate('customer-service')}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
              title="Pre-fill with customer service configuration"
            >
              Customer Service Template
            </button>
            <button
              onClick={() => handleQuickTemplate('sales')}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors dark:text-white"
              title="Pre-fill with sales assistant configuration"
            >
              Sales Assistant Template
            </button>
            <button
              onClick={() => handleQuickTemplate('receptionist')}
              className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors dark:text-white"
              title="Pre-fill with receptionist configuration"
            >
              Receptionist Template
            </button>
          </div>
        </div>
      )}

      {/* Template Selection Indicator */}
      {quickTemplateData && (
        <div className={`mb-4 p-3 rounded-lg border-l-4 border-teal-500 ${theme === 'dark' ? 'bg-teal-900/20' : 'bg-teal-50'
          }`}>
          <div className="flex items-center justify-between">
            <div>
              <h5 className={`font-medium ${theme === 'dark' ? 'text-teal-300' : 'text-teal-800'
                }`}>
                Template Applied: {quickTemplateData.name}
              </h5>
              <p className={`text-sm ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
                }`}>
                {quickTemplateData.description}
              </p>
            </div>
            <button
              onClick={() => setQuickTemplateData(null)}
              className={`text-sm px-2 py-1 rounded ${theme === 'dark'
                  ? 'text-teal-300 hover:bg-teal-800'
                  : 'text-teal-700 hover:bg-teal-100'
                }`}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Parrot Creation Component */}
      <div className={loading ? 'opacity-50 pointer-events-none' : ''}>
        <ParrotCreation
          onComplete={handleParrotComplete}
          onCancel={onCancel}
          onError={onError}
          initialName={quickTemplateData?.name || initialName}
          selectedPhoneNumber={selectedPhoneNumber}
          selectedPhoneNumbers={phoneNumberOptions.selectedPhoneNumbers}
          showNameInput={showNameInput}
          showVoiceSelection={showVoiceSelection}
          mode={mode}
          theme={theme}
          enableProgressSaving={enableProgressSaving}
          customSteps={customSteps}
          onStepChange={handleStepChange}
        />
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center rounded-lg">
          <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-lg dark:bg-gray-800">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
            <span className="text-sm text-gray-700">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedParrotCreation;
